/**
 * 비밀번호 변경 다이얼로그
 */

import { useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { request } from "app/network"
import Swal from "sweetalert2"

interface ChangePasswordDialogProps {
    open: boolean
    onClose: () => void
}

function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps): JSX.Element {
    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [loading, setLoading] = useState(false)

    const handleClose = () => {
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
        onClose()
    }

    const handleSubmit = async () => {
        // 유효성 검사
        if (!currentPassword || !newPassword || !confirmPassword) {
            Swal.fire({
                icon: "warning",
                title: "입력 오류",
                text: "모든 필드를 입력해주세요.",
            })
            return
        }

        if (newPassword !== confirmPassword) {
            Swal.fire({
                icon: "warning",
                title: "입력 오류",
                text: "새 비밀번호가 일치하지 않습니다.",
            })
            return
        }

        if (newPassword.length < 6) {
            Swal.fire({
                icon: "warning",
                title: "입력 오류",
                text: "비밀번호는 최소 6자 이상이어야 합니다.",
            })
            return
        }

        try {
            setLoading(true)
            const response = await request("User.changePassword", {
                paramz: {
                    currentPassword,
                    newPassword,
                },
            })

            if (response.status === 200) {
                await Swal.fire({
                    icon: "success",
                    title: "성공",
                    text: "비밀번호가 성공적으로 변경되었습니다.",
                })
                handleClose()
            } else {
                Swal.fire({
                    icon: "error",
                    title: "오류",
                    text: response.data?.message || "비밀번호 변경에 실패했습니다.",
                })
            }
        } catch (error: any) {
            console.error("비밀번호 변경 실패:", error)
            Swal.fire({
                icon: "error",
                title: "오류",
                text: error.response?.data?.message || "비밀번호 변경 중 오류가 발생했습니다.",
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <MDTypography variant="h5" fontWeight="medium">
                    비밀번호 변경
                </MDTypography>
            </DialogTitle>
            <DialogContent>
                <MDBox pt={2}>
                    <Grid container spacing={3}>
                        {/* 현재 비밀번호 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="password"
                                label="현재 비밀번호"
                                fullWidth
                                value={currentPassword}
                                onChange={(e: any) => setCurrentPassword(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>

                        {/* 새 비밀번호 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="password"
                                label="새 비밀번호"
                                fullWidth
                                value={newPassword}
                                onChange={(e: any) => setNewPassword(e.target.value)}
                                disabled={loading}
                            />
                            <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                                최소 6자 이상 입력해주세요
                            </MDTypography>
                        </Grid>

                        {/* 새 비밀번호 확인 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="password"
                                label="새 비밀번호 확인"
                                fullWidth
                                value={confirmPassword}
                                onChange={(e: any) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={handleClose} color="secondary" disabled={loading}>
                    취소
                </MDButton>
                <MDButton onClick={handleSubmit} color="info" disabled={loading}>
                    {loading ? "변경 중..." : "변경"}
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}

export default ChangePasswordDialog
